// ===================================================================
// Time & Labor — Employee Master (Labor Profiles)
// Shows Ignatius system users as the master list. For each user,
// allows attaching a "Labor Profile" with pay rates, fringe, etc.
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, Select, Modal,
  SimpleGrid, Badge, ActionIcon, LoadingOverlay, NumberInput, Textarea,
  Checkbox,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconDownload, IconUserCircle, IconCurrencyDollar,
  IconUsers, IconPlus,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTimeLaborStore, type LaborEmployee } from '../../store/timeLaborStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { formatPhone } from '../../lib/phoneFormat';
import { exportToCSV } from '../../lib/exportUtils';
import { fetchIgnatiusUsers, WORKER_URL, type IgnatiusUser } from '../../lib/api-switch';

const TEAL = '#0D9488';

const EMPLOYMENT_TYPES = [
  { value: 'permanent_ft', label: 'Permanent Full-Time' },
  { value: 'permanent_pt', label: 'Permanent Part-Time' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'contract', label: 'Contract' },
];

const FLSA_OPTIONS = [
  { value: 'exempt', label: 'Exempt' },
  { value: 'non_exempt', label: 'Non-Exempt' },
];

const BUDGET_OPTIONS = [
  { value: 'budgeted', label: 'Budgeted' },
  { value: 'unbudgeted', label: 'Unbudgeted' },
];

function pf(v: string | number | undefined): number {
  if (typeof v === 'number') return v || 0;
  return parseFloat(stripCurrency(v || '')) || 0;
}

function recalcFringe(form: Partial<LaborEmployee>): Partial<LaborEmployee> {
  const totalFringe = pf(form.fringeFicaPct) + pf(form.fringeRetirementPct) +
    pf(form.fringeHealthPct) + pf(form.fringeWorkersCompPct) +
    pf(form.fringeUnemploymentPct) + pf(form.fringeOtherPct);
  const regRate = pf(form.regularRate);
  const fringeHourly = regRate * totalFringe / 100;
  return {
    ...form,
    totalFringePct: totalFringe.toFixed(2),
    fringeHourlyRate: fringeHourly.toFixed(2),
    totalHourlyRate: (regRate + fringeHourly).toFixed(2),
  };
}

const emptyProfileForm = (): Partial<LaborEmployee> => ({
  employeeName: '', employeeId: '', jobTitle: '', department: '',
  employmentType: 'permanent_ft', flsaStatus: 'non_exempt', budgetStatus: 'budgeted',
  regularRate: '', overtimeRate: '',
  fringeFicaPct: '', fringeRetirementPct: '', fringeHealthPct: '',
  fringeWorkersCompPct: '', fringeUnemploymentPct: '', fringeOtherPct: '',
  totalFringePct: '', fringeHourlyRate: '', totalHourlyRate: '',
  payPolicyRef: '', hireDate: '', normalWeeklyHours: '40',
  rosterEmail: '', organizationId: '',
  defaultAccountCode: '', defaultProgramCode: '',
  status: 'active', notes: '',
});

export default function EmployeeMaster() {
  const { canEdit } = useModulePermission('timelabor');
  const { employees, loadingEmployees, fetchEmployees, createEmployee, updateEmployee, deleteEmployee } = useTimeLaborStore();

  const [users, setUsers] = useState<IgnatiusUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [search, setSearch] = useState('');
  const [filterProfile, setFilterProfile] = useState<string | null>(null);

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IgnatiusUser | null>(null);
  const [existingProfile, setExistingProfile] = useState<LaborEmployee | null>(null);
  const [form, setForm] = useState<Partial<LaborEmployee>>(emptyProfileForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{ user: IgnatiusUser; profile: LaborEmployee } | null>(null);

  // Code lists from KV
  const [accountCodes, setAccountCodes] = useState<{code: string; description: string}[]>([]);
  const [programCodes, setProgramCodes] = useState<{code: string; description: string}[]>([]);

  // Quick Setup
  const [quickSetupOpen, setQuickSetupOpen] = useState(false);
  const [quickSelected, setQuickSelected] = useState<Set<string>>(new Set());
  const [quickRate, setQuickRate] = useState('');
  const [quickOtRate, setQuickOtRate] = useState('');
  const [quickTitle, setQuickTitle] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    fetchEmployees();
    loadUsers();
    loadCodeLists();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCodeLists = async () => {
    const kvH = { Origin: 'https://apps.opengov.com' };
    try {
      const [acRes, pcRes] = await Promise.all([
        fetch(`${WORKER_URL}/api/config/timelabor-account-codes`, { headers: kvH }),
        fetch(`${WORKER_URL}/api/config/timelabor-program-codes`, { headers: kvH }),
      ]);
      if (acRes.ok) { const d = await acRes.json(); if (Array.isArray(d)) setAccountCodes(d); }
      if (pcRes.ok) { const d = await pcRes.json(); if (Array.isArray(d)) setProgramCodes(d); }
    } catch { /* ignore */ }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const list = await fetchIgnatiusUsers();
      setUsers(list);
    } catch (e) {
      console.error('Failed to fetch Ignatius users', e);
    }
    setLoadingUsers(false);
  };

  // Build a map from email -> labor profile
  const profileByEmail = useMemo(() => {
    const m = new Map<string, LaborEmployee>();
    employees.forEach(e => {
      if (e.rosterEmail) m.set((e.rosterEmail || '').toLowerCase(), e);
    });
    return m;
  }, [employees]);

  // Merge users with their profile status
  const mergedList = useMemo(() => {
    return users.map(u => ({
      user: u,
      profile: profileByEmail.get((u.email || '').toLowerCase()) || null,
    }));
  }, [users, profileByEmail]);

  // Filtered
  const filtered = useMemo(() => {
    let list = mergedList;
    if (filterProfile === 'with') list = list.filter(r => r.profile !== null);
    if (filterProfile === 'without') list = list.filter(r => r.profile === null);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.user.fullName || '').toLowerCase().includes(q) ||
        (r.user.email || '').toLowerCase().includes(q) ||
        (r.profile?.jobTitle || '').toLowerCase().includes(q) ||
        (r.profile?.department || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [mergedList, search, filterProfile]);

  const openProfileModal = (user: IgnatiusUser) => {
    setSelectedUser(user);
    const profile = profileByEmail.get((user.email || '').toLowerCase()) || null;
    setExistingProfile(profile);
    if (profile) {
      setForm({ ...profile });
    } else {
      setForm({
        ...emptyProfileForm(),
        employeeName: user.fullName || user.name || user.email,
        rosterEmail: user.email,
      });
    }
    setProfileModalOpen(true);
  };

  const setField = useCallback((key: keyof LaborEmployee, val: string) => {
    setForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === 'regularRate' || (key.startsWith('fringe') && key.endsWith('Pct'))) {
        return recalcFringe(next);
      }
      return next;
    });
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    const data = recalcFringe(form);
    if (existingProfile) {
      await updateEmployee(existingProfile.Rid, data);
      notifications.show({ title: 'Updated', message: `Labor profile updated for ${selectedUser?.fullName || selectedUser?.name || selectedUser?.email}`, color: TEAL });
    } else {
      const result = await createEmployee({
        ...data,
        employeeName: selectedUser?.fullName || selectedUser?.name || data.employeeName || '',
        rosterEmail: selectedUser?.email || data.rosterEmail || '',
      });
      if (result) {
        notifications.show({ title: 'Created', message: `Labor profile created for ${selectedUser?.fullName || selectedUser?.name || selectedUser?.email}`, color: TEAL });
      }
    }
    setSaving(false);
    setProfileModalOpen(false);
  };

  const handleDeleteProfile = async () => {
    if (!deleteTarget) return;
    await deleteEmployee(deleteTarget.profile.Rid);
    notifications.show({ title: 'Deleted', message: `Labor profile removed for ${deleteTarget.user.fullName}`, color: 'red' });
    setDeleteTarget(null);
  };

  const handleExport = () => {
    const withProfiles = mergedList.filter(r => r.profile);
    exportToCSV('labor-profiles', [
      'Name', 'Email', 'Role', 'Employee ID', 'Title', 'Department', 'Type', 'FLSA',
      'Regular Rate', 'OT Rate', 'Total Fringe %', 'Total Rate', 'Status',
    ], withProfiles.map(r => [
      r.user.fullName, r.user.email, r.user.role,
      r.profile!.employeeId, r.profile!.jobTitle, r.profile!.department,
      r.profile!.employmentType, r.profile!.flsaStatus,
      r.profile!.regularRate, r.profile!.overtimeRate,
      r.profile!.totalFringePct, r.profile!.totalHourlyRate,
      r.profile!.status,
    ]));
  };

  // Quick Setup: users without profiles
  const usersWithoutProfile = useMemo(() =>
    mergedList.filter(r => !r.profile),
    [mergedList]
  );

  const handleQuickSetup = async () => {
    if (quickSelected.size === 0) {
      notifications.show({ title: 'Required', message: 'Select at least one user', color: 'red' });
      return;
    }
    setQuickSaving(true);
    let created = 0;
    for (const email of quickSelected) {
      const user = users.find(u => u.email === email);
      if (!user) continue;
      await createEmployee({
        employeeName: user.fullName || user.name || user.email,
        rosterEmail: user.email,
        regularRate: quickRate || '',
        overtimeRate: quickOtRate || '',
        jobTitle: quickTitle || '',
        status: 'active',
        employmentType: 'permanent_ft',
        flsaStatus: 'non_exempt',
        budgetStatus: 'budgeted',
        normalWeeklyHours: '40',
      });
      created++;
    }
    notifications.show({
      title: 'Quick Setup Complete',
      message: `${created} labor profile(s) created`,
      color: TEAL,
    });
    setQuickSaving(false);
    setQuickSetupOpen(false);
    setQuickSelected(new Set());
    setQuickRate('');
    setQuickOtRate('');
    setQuickTitle('');
  };

  const toggleQuickSelect = (email: string) => {
    setQuickSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const toggleAllQuick = () => {
    if (quickSelected.size === usersWithoutProfile.length) {
      setQuickSelected(new Set());
    } else {
      setQuickSelected(new Set(usersWithoutProfile.map(r => r.user.email)));
    }
  };

  const profileCount = mergedList.filter(r => r.profile).length;

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={(loadingEmployees && employees.length === 0) || (loadingUsers && users.length === 0)} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Employee Master</Title>
          <Text size="sm" c="dimmed">
            Ignatius users with optional labor profiles for pay rates and fringe benefits
          </Text>
        </div>
        <Group gap={6}>
          {canEdit && (
            <>
              <Button variant="light" color="gray" leftSection={<IconDownload size={14} />} onClick={handleExport}>
                Export CSV
              </Button>
              {usersWithoutProfile.length > 0 && (
                <Button variant="light" color={TEAL} leftSection={<IconPlus size={14} />}
                  onClick={() => setQuickSetupOpen(true)}>
                  Quick Setup ({usersWithoutProfile.length})
                </Button>
              )}
            </>
          )}
        </Group>
      </Group>

      {/* Filters */}
      <Group gap={8} mb="md">
        <TextInput
          placeholder="Search by name, email, title..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 300 }}
          size="xs"
        />
        <Select size="xs" placeholder="All Users" data={[
          { value: 'with', label: 'With Labor Profile' },
          { value: 'without', label: 'Without Labor Profile' },
        ]} value={filterProfile} onChange={setFilterProfile} clearable style={{ width: 180 }} />
        <Text size="xs" c="dimmed">
          {filtered.length} user(s) — {profileCount} with profiles
        </Text>
      </Group>

      {/* Table */}
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>System Role</Table.Th>
              <Table.Th>Labor Profile</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Reg Rate</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Total Rate</Table.Th>
              {canEdit && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text size="sm" c="dimmed" ta="center" py="lg">No users found.</Text>
                </Table.Td>
              </Table.Tr>
            ) : filtered.map(({ user, profile }) => (
              <Table.Tr key={user.id} style={{ cursor: canEdit ? 'pointer' : undefined }}
                onClick={() => canEdit && openProfileModal(user)}>
                <Table.Td fw={500}>{user.fullName || user.name || user.email}</Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{user.email}</Text></Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color="gray">{user.role || user.topLevelRole || 'User'}</Badge>
                </Table.Td>
                <Table.Td>
                  {profile ? (
                    <Badge size="xs" color={TEAL} variant="filled">Has Profile</Badge>
                  ) : (
                    <Badge size="xs" color="gray" variant="light">No Profile</Badge>
                  )}
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  {profile ? formatCurrency(profile.regularRate) : '-'}
                </Table.Td>
                <Table.Td style={{ textAlign: 'right', fontWeight: profile ? 600 : 400 }}>
                  {profile ? formatCurrency(profile.totalHourlyRate) : '-'}
                </Table.Td>
                {canEdit && (
                  <Table.Td>
                    <ActionIcon size="xs" variant="subtle" color={TEAL}
                      onClick={e => { e.stopPropagation(); openProfileModal(user); }}>
                      {profile ? <IconCurrencyDollar size={14} /> : <IconPlus size={14} />}
                    </ActionIcon>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Profile Modal */}
      <Modal opened={profileModalOpen} onClose={() => setProfileModalOpen(false)} size="lg"
        title={existingProfile ? `Edit Labor Profile: ${selectedUser?.fullName || selectedUser?.name || selectedUser?.email}` : `Create Labor Profile: ${selectedUser?.fullName || selectedUser?.name || selectedUser?.email}`}>

        {/* Read-only User Info */}
        <Paper p="sm" withBorder bg="gray.0" mb="md">
          <Group gap={8} mb={4}>
            <IconUserCircle size={16} color={TEAL} />
            <Text size="sm" fw={600}>User Info (read-only from Ignatius)</Text>
          </Group>
          <SimpleGrid cols={2} spacing="xs">
            <div><Text size="xs" c="dimmed">Name</Text><Text size="sm">{selectedUser?.fullName || selectedUser?.name || selectedUser?.email}</Text></div>
            <div><Text size="xs" c="dimmed">Email</Text><Text size="sm">{selectedUser?.email}</Text></div>
            <div><Text size="xs" c="dimmed">Phone</Text><Text size="sm">{selectedUser?.phoneNumber ? formatPhone(selectedUser.phoneNumber) : '-'}</Text></div>
            <div><Text size="xs" c="dimmed">Role</Text><Text size="sm">{selectedUser?.role || selectedUser?.topLevelRole || '-'}</Text></div>
          </SimpleGrid>
        </Paper>

        {/* Labor Profile (editable) */}
        <Group gap={8} mb="sm">
          <IconCurrencyDollar size={16} color={TEAL} />
          <Text size="sm" fw={600}>Labor Profile</Text>
        </Group>

        <SimpleGrid cols={2} spacing="sm" mb="md">
          <TextInput label="Employee ID" value={form.employeeId || ''}
            onChange={e => setField('employeeId', e.target.value)} size="sm" />
          <TextInput label="Job Title" value={form.jobTitle || ''}
            onChange={e => setField('jobTitle', e.target.value)} size="sm" />
          <TextInput label="Department" value={form.department || ''}
            onChange={e => setField('department', e.target.value)} size="sm" />
          <Select label="Employment Type" data={EMPLOYMENT_TYPES} size="sm"
            value={form.employmentType || 'permanent_ft'}
            onChange={v => setField('employmentType', v || 'permanent_ft')} />
          <Select label="FLSA Status" data={FLSA_OPTIONS} size="sm"
            value={form.flsaStatus || 'non_exempt'}
            onChange={v => setField('flsaStatus', v || 'non_exempt')} />
          <Select label="Budget Status" data={BUDGET_OPTIONS} size="sm"
            value={form.budgetStatus || 'budgeted'}
            onChange={v => setField('budgetStatus', v || 'budgeted')} />
        </SimpleGrid>

        <Text size="sm" fw={600} mb="xs">Pay Rates</Text>
        <SimpleGrid cols={2} spacing="sm" mb="md">
          <TextInput label="Regular Rate" value={formatCurrency(form.regularRate || '')} size="sm"
            onChange={e => setField('regularRate', stripCurrency(e.target.value))}
            styles={{ input: { fontFamily: 'monospace' } }} />
          <TextInput label="Overtime Rate" value={formatCurrency(form.overtimeRate || '')} size="sm"
            onChange={e => setField('overtimeRate', stripCurrency(e.target.value))}
            styles={{ input: { fontFamily: 'monospace' } }} />
        </SimpleGrid>

        <Text size="sm" fw={600} mb="xs">Fringe Benefit Rates (%)</Text>
        <SimpleGrid cols={3} spacing="sm" mb="md">
          <NumberInput label="FICA %" value={pf(form.fringeFicaPct)} decimalScale={2} size="sm"
            onChange={v => setField('fringeFicaPct', String(v))} min={0} max={100} />
          <NumberInput label="Retirement %" value={pf(form.fringeRetirementPct)} decimalScale={2} size="sm"
            onChange={v => setField('fringeRetirementPct', String(v))} min={0} max={100} />
          <NumberInput label="Health %" value={pf(form.fringeHealthPct)} decimalScale={2} size="sm"
            onChange={v => setField('fringeHealthPct', String(v))} min={0} max={100} />
          <NumberInput label="Workers' Comp %" value={pf(form.fringeWorkersCompPct)} decimalScale={2} size="sm"
            onChange={v => setField('fringeWorkersCompPct', String(v))} min={0} max={100} />
          <NumberInput label="Unemployment %" value={pf(form.fringeUnemploymentPct)} decimalScale={2} size="sm"
            onChange={v => setField('fringeUnemploymentPct', String(v))} min={0} max={100} />
          <NumberInput label="Other %" value={pf(form.fringeOtherPct)} decimalScale={2} size="sm"
            onChange={v => setField('fringeOtherPct', String(v))} min={0} max={100} />
        </SimpleGrid>

        <Paper p="sm" withBorder bg="gray.0" mb="md">
          <SimpleGrid cols={3}>
            <div>
              <Text size="xs" c="dimmed">Total Fringe %</Text>
              <Text fw={700}>{pf(form.totalFringePct).toFixed(2)}%</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Fringe $/hr</Text>
              <Text fw={700}>{formatCurrency(form.fringeHourlyRate || '0')}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Total $/hr (Reg + Fringe)</Text>
              <Text fw={700} c={TEAL}>{formatCurrency(form.totalHourlyRate || '0')}</Text>
            </div>
          </SimpleGrid>
        </Paper>

        <SimpleGrid cols={2} spacing="sm" mb="md">
          <TextInput label="Pay Policy Reference" value={form.payPolicyRef || ''} size="sm"
            onChange={e => setField('payPolicyRef', e.target.value)} />
          <TextInput label="Hire Date" type="date" value={form.hireDate || ''} size="sm"
            onChange={e => setField('hireDate', e.target.value)} />
          <NumberInput label="Normal Weekly Hours" value={pf(form.normalWeeklyHours) || 40} size="sm"
            onChange={v => setField('normalWeeklyHours', String(v))} min={0} max={168} />
        </SimpleGrid>

        {/* Default Codes */}
        {(accountCodes.length > 0 || programCodes.length > 0) && (
          <>
            <Text size="sm" fw={600} mb="xs">Default Codes</Text>
            <SimpleGrid cols={2} spacing="sm" mb="md">
              {accountCodes.length > 0 && (
                <Select label="Default Account Code" size="sm" clearable searchable
                  placeholder="Select account code..."
                  data={accountCodes.filter(c => c.code).map(c => ({ value: String(c.code), label: String(`${c.code} — ${c.description || c.code}`) }))}
                  value={form.defaultAccountCode || null}
                  onChange={v => setField('defaultAccountCode', v || '')} />
              )}
              {programCodes.length > 0 && (
                <Select label="Default Program Code" size="sm" clearable searchable
                  placeholder="Select program code..."
                  data={programCodes.filter(c => c.code).map(c => ({ value: String(c.code), label: String(`${c.code} — ${c.description || c.code}`) }))}
                  value={form.defaultProgramCode || null}
                  onChange={v => setField('defaultProgramCode', v || '')} />
              )}
            </SimpleGrid>
          </>
        )}

        <Textarea label="Notes" value={form.notes || ''} minRows={2} size="sm"
          onChange={e => setField('notes', e.target.value)} mb="md" />

        <Group justify="space-between">
          <div>
            {existingProfile && canEdit && (
              <Button variant="light" color="red" size="sm"
                onClick={() => {
                  setProfileModalOpen(false);
                  setDeleteTarget({ user: selectedUser!, profile: existingProfile });
                }}>
                Remove Profile
              </Button>
            )}
          </div>
          <Group gap={8}>
            <Button variant="light" color="gray" onClick={() => setProfileModalOpen(false)}>Cancel</Button>
            <Button color={TEAL} onClick={handleSaveProfile} loading={saving}>
              {existingProfile ? 'Save Changes' : 'Create Labor Profile'}
            </Button>
          </Group>
        </Group>
      </Modal>

      {/* Delete Profile Confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Labor Profile" size="sm">
        <Text size="sm" mb="md">
          Remove the labor profile for <strong>{deleteTarget?.user.fullName}</strong>?
          This deletes pay rates and fringe data. The user account is not affected.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" color="gray" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDeleteProfile}>Remove Profile</Button>
        </Group>
      </Modal>

      {/* Quick Setup Modal */}
      <Modal opened={quickSetupOpen} onClose={() => setQuickSetupOpen(false)} size="lg"
        title="Quick Setup — Create Labor Profiles">
        <Text size="sm" c="dimmed" mb="md">
          Select users and set default rates. Individual profiles can be edited afterward.
        </Text>

        <SimpleGrid cols={3} spacing="sm" mb="md">
          <TextInput label="Default Regular Rate" value={formatCurrency(quickRate)} size="sm"
            onChange={e => setQuickRate(stripCurrency(e.target.value))} placeholder="$25.00"
            styles={{ input: { fontFamily: 'monospace' } }} />
          <TextInput label="Default OT Rate" value={formatCurrency(quickOtRate)} size="sm"
            onChange={e => setQuickOtRate(stripCurrency(e.target.value))} placeholder="$37.50"
            styles={{ input: { fontFamily: 'monospace' } }} />
          <TextInput label="Default Job Title" value={quickTitle} size="sm"
            onChange={e => setQuickTitle(e.target.value)} placeholder="e.g. Field Worker" />
        </SimpleGrid>

        <Paper withBorder style={{ maxHeight: 350, overflowY: 'auto' }}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox size="xs" checked={quickSelected.size === usersWithoutProfile.length && usersWithoutProfile.length > 0}
                    indeterminate={quickSelected.size > 0 && quickSelected.size < usersWithoutProfile.length}
                    onChange={toggleAllQuick} />
                </Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {usersWithoutProfile.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c="dimmed" ta="center" py="md">All active users already have labor profiles.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : usersWithoutProfile.map(({ user }) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Checkbox size="xs" checked={quickSelected.has(user.email)}
                      onChange={() => toggleQuickSelect(user.email)} />
                  </Table.Td>
                  <Table.Td>{user.fullName || user.name || user.email}</Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{user.email}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light" color="gray">{user.role || 'User'}</Badge></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        <Group justify="space-between" mt="md">
          <Text size="xs" c="dimmed">{quickSelected.size} selected</Text>
          <Group gap={8}>
            <Button variant="light" color="gray" onClick={() => setQuickSetupOpen(false)}>Cancel</Button>
            <Button color={TEAL} onClick={handleQuickSetup} loading={quickSaving}
              leftSection={<IconUsers size={14} />}>
              Create {quickSelected.size} Profile(s)
            </Button>
          </Group>
        </Group>
      </Modal>
    </div>
  );
}
